import React, { useEffect, useState } from "react";
import { adminApi } from "@/api/adminOneClient";

const TIER_COLORS = { free: "#60B8E8", professional: "#7AC860", executive: "#F5C842", enterprise: "#E8A020" };

function money(n) {
  if (n == null) return "—";
  if (n >= 1000) return `$${(n/1000).toFixed(1)}k`;
  return `$${n}`;
}

export default function AdminMembershipAnalytics() {
  const [data, setData] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState("");
  const [toast, setToast] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const [analyticsRes, auditRes, usersRes] = await Promise.all([
        adminApi.membershipAnalytics(),
        adminApi.membershipAudit(40),
        adminApi.users({ limit: 200 }).catch(() => ({ data: { users: [] } })),
      ]);
      setData(analyticsRes.data);
      setAudit(auditRes.data?.audit || []);
      setUsers(usersRes.data?.users || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const setTier = async (uid, tier) => {
    setBusy(`${uid}:${tier}`);
    try {
      await adminApi.setUserTierEx(uid, { tier, source: "manual", status: "active" });
      setToast(`Set ${uid} → ${tier}`);
      await refresh();
    } catch {
      setToast(`Failed to set ${uid}`);
    } finally {
      setBusy("");
      setTimeout(() => setToast(""), 5000);
    }
  };

  if (loading) return <div className="admin-page mesh-shell" data-testid="membership-analytics"><div className="admin-saved">Loading membership analytics…</div></div>;

  const dist = data?.tier_distribution;
  const rev = data?.revenue;
  const funnel = data?.funnel;
  const distMax = Math.max(1, ...(dist?.rows || []).map((r) => r.count));
  const funnelMax = Math.max(1, ...(funnel?.stages || []).map((s) => s.count));

  return (
    <div className="admin-page mesh-shell" data-testid="membership-analytics">
      <div className="mesh-bg-radar" aria-hidden="true" />
      <div className="mesh-bg-particles" aria-hidden="true" />

      <div className="admin-page__head">
        <h2 className="admin-page__title">Membership Analytics</h2>
        <div className="mesh-eyebrow">v3 Constitution · Soft gating · No payment processor wired</div>
      </div>

      {/* Revenue Dashboard */}
      <section className="admin-section mesh-section" data-testid="membership-revenue">
        <h3 className="admin-section__title">Revenue Dashboard · MOCKED (no billing)</h3>
        <div className="mesh-exec-grid">
          <div className="mesh-exec-cell"><div className="mesh-exec-cell__num">{money(rev?.mrr_usd)}</div><div className="mesh-exec-cell__lbl">MRR (USD)</div></div>
          <div className="mesh-exec-cell"><div className="mesh-exec-cell__num">{money(rev?.arr_usd)}</div><div className="mesh-exec-cell__lbl">ARR (USD)</div></div>
          <div className="mesh-exec-cell"><div className="mesh-exec-cell__num">{money(rev?.arpu_usd)}</div><div className="mesh-exec-cell__lbl">ARPU</div></div>
          <div className="mesh-exec-cell"><div className="mesh-exec-cell__num">{rev?.paid_users ?? 0}</div><div className="mesh-exec-cell__lbl">Paid Users</div></div>
          <div className="mesh-exec-cell"><div className="mesh-exec-cell__num">{rev?.total_users ?? 0}</div><div className="mesh-exec-cell__lbl">Total Users</div></div>
        </div>
      </section>

      {/* Tier Distribution */}
      <section className="admin-section mesh-section" data-testid="membership-distribution">
        <h3 className="admin-section__title">Tier Distribution</h3>
        <div className="mship-bars">
          {(dist?.rows || []).map((r) => (
            <div key={r.tier} className="mship-bar-row" data-testid={`tier-dist-${r.tier}`}>
              <div className="mship-bar-row__lbl" style={{ color: TIER_COLORS[r.tier] }}>{r.label}</div>
              <div className="mship-bar-row__bar">
                <span style={{ width: `${(r.count / distMax) * 100}%`, background: TIER_COLORS[r.tier] }} />
              </div>
              <div className="mship-bar-row__num">{r.count} <span className="mship-bar-row__pct">({r.pct}%)</span></div>
              <div className="mship-bar-row__price">{r.price_usd === 0 ? "FREE" : `$${r.price_usd}/mo`}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Conversion Funnel */}
      <section className="admin-section mesh-section" data-testid="membership-funnel">
        <h3 className="admin-section__title">Conversion Funnel</h3>
        <div className="mship-funnel">
          {(funnel?.stages || []).map((s, i) => (
            <div key={s.stage} className="mship-funnel__stage" data-testid={`funnel-stage-${s.stage.toLowerCase()}`} style={{ width: `${Math.max(20, (s.count / funnelMax) * 100)}%`, background: `linear-gradient(90deg, ${TIER_COLORS[s.stage.toLowerCase()] || "#7AC860"}, transparent)` }}>
              <span className="mship-funnel__name">{s.stage}</span>
              <span className="mship-funnel__num">{s.count}</span>
              <span className="mship-funnel__pct">{s.of_total_pct}% of total</span>
            </div>
          ))}
        </div>
      </section>

      {/* User Tier Manager */}
      <section className="admin-section mesh-section" data-testid="membership-users">
        <h3 className="admin-section__title">User Tier Management</h3>
        {toast && <div className="mesh-toast" data-testid="membership-toast">{toast}</div>}
        <table className="admin-table" data-testid="membership-users-table">
          <thead><tr><th>User</th><th>Role</th><th>Current Tier</th><th>Source</th><th>Set tier</th></tr></thead>
          <tbody>
            {users.slice(0, 20).map((u) => (
              <tr key={u.id || u._id || u.email}>
                <td>{u.name || u.email}</td>
                <td><span className="mesh-pill mesh-pill--ghost">{u.role || "user"}</span></td>
                <td><span className="mesh-pill" style={{ borderColor: TIER_COLORS[u.tier] || "#7AC860", color: TIER_COLORS[u.tier] || "#7AC860" }}>{(u.tier || "professional").toUpperCase()}</span></td>
                <td>{u.tier_source || "—"}</td>
                <td>
                  <div className="mship-tier-pick">
                    {["free","professional","executive","enterprise"].map((t) => (
                      <button key={t}
                              type="button"
                              className="admin-linkbtn"
                              disabled={busy === `${u.id || u._id}:${t}` || u.role === "super_admin"}
                              onClick={() => setTier(u.id || u._id, t)}
                              data-testid={`set-tier-${u.id || u._id}-${t}`}>{t}</button>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
            {users.length === 0 && <tr><td colSpan={5} className="admin-foot-note">No users.</td></tr>}
          </tbody>
        </table>
      </section>

      {/* Audit Trail */}
      <section className="admin-section mesh-section" data-testid="membership-audit">
        <h3 className="admin-section__title">Membership Audit Trail</h3>
        <ol className="mesh-timeline">
          {audit.map((ev) => (
            <li key={ev.id || ev._id} className="mesh-timeline__item" style={{ borderColor: ev.event === "membership_denied" ? "#C84A3A" : ev.event === "membership_upgrade" ? "#E8A020" : "#7AC860" }}>
              <span className="mesh-timeline__time">{new Date(ev.created_at).toLocaleTimeString()}</span>
              <span className="mesh-pill mesh-pill--ghost">{ev.event}</span>
              {ev.feature && <span className="mesh-pill mesh-pill--gold">{ev.feature}</span>}
              {ev.required_tier && <span className="mesh-pill mesh-pill--ghost">need {ev.required_tier}</span>}
              {ev.actual_tier && <span className="mesh-pill mesh-pill--ghost">had {ev.actual_tier}</span>}
              <span className="mesh-timeline__title">{ev.user_id || ev.actor}</span>
            </li>
          ))}
          {audit.length === 0 && <div className="admin-foot-note">No membership audit events yet.</div>}
        </ol>
      </section>
    </div>
  );
}
